import {
  AuthContextProvider,
  CommonContextProvider,
  composeProviders,
  GamesContextProvider,
  PrimarySettingsProvider,
  useCreateNoscript,
  useCreateScript,
  useInlineScript,
} from '@repo-level/core-front';
import { gtm } from 'assets/noscripts/gtm';
import { ErrorBoundary } from 'components/internal/ErrorBoundary';
import { InternalError } from 'components/internal/InternalError';
import { GlobalStyles } from 'components/styles/GlobalStyles';
import { GradientsList } from 'components/styles/GradientsList';
import { AddonsContextProvider } from 'contexts/addons';
import { BackgroundContextProvider } from 'contexts/background';
import { BonanzaContextProvider } from 'contexts/bonanza';
import { CloverGoldContextProvider } from 'contexts/cloverGold';
import { CommonUtilsContextProvider } from 'contexts/commonUtils';
import { ConfigContextProvider } from 'contexts/config';
import { HotjarContextProvider } from 'contexts/hotjar';
import { IntercomContextProvider } from 'contexts/intercom';
import { MissionsContextProvider } from 'contexts/missions';
import { ModalsContextProvider } from 'contexts/modal';
import { NotificationsContextProvider } from 'contexts/notifications';
import { OneSignalContextProvider } from 'contexts/oneSignal';
import { ReferralProgramContextProvider } from 'contexts/referralProgram';
import { Restricted } from 'contexts/restricted';
import { LobbySliderContextProvider } from 'contexts/slider';
import { StagContextProvider } from 'contexts/stag';
import { StardustRushContextProvider } from 'contexts/stardustRush/stardustRush';
import { TabBarContextProvider } from 'contexts/tabBarSize';
import { TournamentsContextProvider } from 'contexts/tournaments';
import { BONUS_RESTRICTED_COUNTRIES } from 'helpers/conf';
import { BONUS_RESTRICTED_GROUPS, DEFAULT_NEW_USER_GROUPS } from 'helpers/user';
import { useServiceWorker } from 'hooks/useServiceWorkerHook';
import { isPrerender } from 'modules/common/helpers/prerender';
import { MobileAppContextProvider } from 'modules/mobileApp/context';
import type { FC } from 'react';
import { Routes } from 'routes';
import { StyleSheetManager, ThemeProvider } from 'styled-components';
import type { Theme } from 'theme/contracts';
import { RealTimeServiceContextProvider } from './contexts/realTimeService';

interface AppProps {
  theme: Theme;
}

const PrimarySettingsContextProvider: FC = ({ children }) => (
  <PrimarySettingsProvider
    defaultValues={{
      defaultUserStatuses: DEFAULT_NEW_USER_GROUPS,
      bonusRestrictedGroups: BONUS_RESTRICTED_GROUPS,
      bonusRestrictedCountries: BONUS_RESTRICTED_COUNTRIES,
    }}
  >
    {children}
  </PrimarySettingsProvider>
);

const Provider = composeProviders([
  PrimarySettingsContextProvider,
  CommonContextProvider,
  AuthContextProvider,
  ConfigContextProvider,
  StagContextProvider,
  OneSignalContextProvider,
  TournamentsContextProvider,
  GamesContextProvider,
  BackgroundContextProvider,
  ModalsContextProvider,
  IntercomContextProvider,
  CommonUtilsContextProvider,
  RealTimeServiceContextProvider,
  AddonsContextProvider,
  MissionsContextProvider,
  HotjarContextProvider,
  LobbySliderContextProvider,
  TabBarContextProvider,
  Restricted,
  BonanzaContextProvider,
  CloverGoldContextProvider,
  MobileAppContextProvider,
  StardustRushContextProvider,
  ReferralProgramContextProvider,
  NotificationsContextProvider,
]);

const App: FC<AppProps> = ({ theme }) => {
  useCreateScript(
    '/pixels/google-tag-manager.js',
    window.appConfig.APP_ENV !== 'development'
  );

  useInlineScript(
    `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${window.appConfig.GA_MEASUREMENT_ID}', {
      page_path: window.location.pathname,
      hostname: window.location.hostname
    });
  `,
    !!window.appConfig.GA_MEASUREMENT_ID &&
      window.appConfig.APP_ENV !== 'development',
    'google-analytics'
  );

  useServiceWorker();
  useCreateNoscript(gtm, window.appConfig.APP_ENV !== 'development');

  // Disable CSSOM injection for prerender - forces styles to be written as text
  // so they appear in serialized HTML. Without this, styled-components uses
  // insertRule() which doesn't persist in HTML snapshots.
  const disableCSSOM = isPrerender();

  return (
    <StyleSheetManager disableCSSOMInjection={disableCSSOM}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />

        <ErrorBoundary fallback={<InternalError />}>
          <Provider>
            <Routes />
          </Provider>
        </ErrorBoundary>
        <GradientsList />
      </ThemeProvider>
    </StyleSheetManager>
  );
};

export default App;
